import React from 'react';
import PropTypes from 'prop-types';
import { map, uniqueId } from 'lodash';
import styled, { css } from 'styled-components';
import { injectIntl, FormattedMessage } from 'react-intl';
import { darken } from 'polished';
import { ArrowDown, ArrowUp } from '@origo_vl_banking/icons';
import Container from './components/Container';
import LabelContainer from './components/LabelContainer';
import StyledLabel from './components/StyledLabel';

const SelectDropdownContainer = styled(Container)`
  position: relative;
  width: ${props => props.width};
`;

const HeaderContainer = styled.div`
  background: ${props => (props.background ? props.background : '#fff')};
  color: ${props => (props.color ? props.color : 'unset')};
  cursor: ${props => (props.disabled ? 'no-drop' : 'default')};
  border-radius: ${({ theme }) => theme.borderRadius || '0px'};
  border-radius: ${({ theme, expanded }) =>
    theme.borderRadius && expanded && `${theme.borderRadius} ${theme.borderRadius} 0px 0px`};
  ${({ error, theme, focused }) => {
    if (error) {
      return `
        border: 3px solid ${theme.danger};
        transition: 250ms;
        `;
    } else if (focused) {
      return `border: thin solid ${theme.primaryColor}; transition: 250ms`;
    }
    return `border: thin solid ${theme.borderColor}; transition: 250ms`;
  }};
  font-size: ${props => props.headerFontSize || '16px'};
  max-height: 300px;
  overflow-x: hidden;
  overflow-y: auto;
  text-overflow: ellipsis;
  white-space: nowrap;
  outline: none;
  &:focus {
    ${props => props.expanded && 'outline: none;'};
  }

  ${({ disabled }) =>
    disabled &&
    css`
      background: ${props => props.theme.disabledInputBackground || '#eee'};
      color: ${props =>
    (props.theme.disabledInputBackground
      ? darken(0.3, props.theme.disabledInputBackground)
      : '#aaa')};
    `};
`;

const SelectHeader = styled.div`
  align-items: center;
  display: flex;
  height: ${props => props.headerHeight};
  justify-content: ${props => (props.value === '' ? 'flex-end' : 'space-between')};
  padding: 8px;
  user-select: none;
  pointer-events: ${props => (props.disabled ? 'none' : 'unset')};
`;

const OptionsContainer = styled(HeaderContainer)`
  border-radius: ${props => (props.expanded ? '0px 0px 8px 8px' : '8px')};
  border-top: 0;
  display: inherit;
  font-size: ${props => props.optionsFontSize};
  padding-bottom: 4px;
  position: absolute;
  width: calc(100% - 2px);
  z-index: 100;
  height: ${({ height }) => height && height};
  background: ${props => (props.background ? props.background : '#fff')};
  color: ${props => (props.color ? props.color : 'unset')};

  ${({ height }) =>
    height &&
    css`
      &::-webkit-scrollbar-thumb {
        border-radius: 8px;
        background-color: rgba(0, 0, 0, 0.5);
        -webkit-box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
        box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
      }

      &::-webkit-scrollbar {
        -webkit-appearance: none;
        width: 10px;
      }
    `};
`;

const OptionHeaderContainer = styled.li`
  cursor: default;
  font-weight: bold;

  padding: 4px 8px;
  user-select: none;
`;

const OptionContainer = styled(OptionHeaderContainer)`
  cursor: pointer;
  font-weight: normal;
  overflow-x: hidden;
  text-indent: 8px;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-height: ${props => props.optionsFontSize || '16px'};

  &:hover {
    background-color: ${props => (props.hoverColor ? props.hoverColor : props.theme.primaryColor)};
    color: ${({ hoverTextColor }) => hoverTextColor || 'white'};
  }
`;

const SelectedItem = styled(OptionContainer)`
  background-color: ${props => (props.hoverColor ? props.hoverColor : props.theme.primaryColor)};
  color: ${({ hoverTextColor }) => hoverTextColor || 'white'};
`;

class SelectDropdown extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = { expanded: false, focused: false, selected: '' };
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClick, false);
  }

  componentDidUpdate(prevProps, prevState) {
    const focusedItem = document.getElementById('focusedItem');
    const optionsContainer = document.getElementById('optionsContainer');
    if (focusedItem) {
      if (focusedItem.offsetTop < optionsContainer.scrollTop) {
        focusedItem.scrollIntoView();
      } else if (
        focusedItem.offsetTop + focusedItem.offsetHeight >
        optionsContainer.scrollTop + optionsContainer.offsetHeight
      ) {
        if (window.navigator.userAgent.includes('Edge')) {
          focusedItem.scrollIntoView();
        } else {
          optionsContainer.scrollTo(
            0,
            focusedItem.offsetTop + focusedItem.offsetHeight - optionsContainer.offsetHeight,
          );
        }
      }
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClick, false);
  }

  handleClick = (e) => {
    const { expanded } = this.state;

    if (expanded && !this.multiSelectRef.contains(e.target)) {
      this.toggleExpand();
      e.stopPropagation();
    }
  };

  handlePropagation = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  handleOnClickOrTab = (selected) => {
    if (selected !== '') {
      this.handleOnClick(selected);
    } else if (selected === '') {
      this.toggleExpand();
    }
  };

  handleKeyPress = (e) => {
    const { expanded, focused, selected } = this.state;
    if (e.keyCode === 40 && focused) {
      // Arrow Down
      if (!expanded) {
        this.toggleExpand();
        this.handlePropagation(e);
      }
      if (expanded) {
        this.tabNextValue();
        this.handlePropagation(e);
      }
    } else if (e.keyCode === 38 && focused) {
      // Arrow Up
      if (expanded) {
        this.tabPreviousValue();
        this.handlePropagation(e);
      }
    } else if (e.keyCode === 9 && expanded && focused) {
      // Tab
      this.handleOnClickOrTab(selected);
    } else if (e.keyCode === 13 && focused) {
      // Enter
      this.handleOnClickOrTab(selected);
    }
  };

  handleOnClick = (childKey) => {
    const { onClicked } = this.props;

    onClicked(childKey);
    this.toggleExpand();
    this.setState({ selected: '' });
  };

  addKeyPressListener = () => {
    this.toggleFocused();
    document.addEventListener('keydown', this.handleKeyPress, false);
  };

  removeKeyPressListener = () => {
    this.toggleFocused();
    document.removeEventListener('keydown', this.handleKeyPress, false);
  };

  toggleExpand() {
    this.setState(prevState => ({ expanded: !prevState.expanded }));
  }

  toggleFocused() {
    this.setState(prevState => ({ focused: !prevState.focused }));
  }

  clearSelected = () => {
    this.setState({ selected: '' });
  };

  tabNextValue = () => {
    const { data, grouped, dataKey } = this.props;
    const { selected } = this.state;

    if (!grouped) {
      if (selected === '') {
        this.setState({ selected: data[0][dataKey] });
      }

      const selectedItem = data.find(item => item[dataKey] === selected);
      const indexOfSelected = data.indexOf(selectedItem);
      if (indexOfSelected + 1 < data.length) {
        this.setState({ selected: data[indexOfSelected + 1][dataKey] });
      }
    } else if (grouped) {
      if (selected === '') {
        this.setState({ selected: data[0].children[0][dataKey] });
      }

      const childList = [];
      map(data, (item) => {
        map(item.children, (child) => {
          childList.push(child);
        });
      });
      const selectedItem = childList.find(item => item[dataKey] === selected);
      const indexOfSelected = childList.indexOf(selectedItem);
      if (indexOfSelected + 1 < childList.length) {
        this.setState({ selected: childList[indexOfSelected + 1][dataKey] });
      }
    }
  };

  tabPreviousValue = () => {
    const { data, grouped, dataKey } = this.props;
    const { selected } = this.state;

    if (!grouped) {
      const selectedItem = data.find(item => item[dataKey] === selected);
      const indexOfSelected = data.indexOf(selectedItem);
      if (indexOfSelected > 0) {
        this.setState({ selected: data[indexOfSelected - 1][dataKey] });
      }
    } else if (grouped) {
      const childList = [];
      map(data, (item) => {
        map(item.children, (child) => {
          childList.push(child);
        });
      });

      const selectedItem = childList.find(item => item[dataKey] === selected);
      const indexOfSelected = childList.indexOf(selectedItem);
      if (indexOfSelected > 0) {
        this.setState({ selected: childList[indexOfSelected - 1][dataKey] });
      }
    }
  };

  renderValue = (value) => {
    const { intl, formatMessage } = this.props;

    return formatMessage ? intl.formatMessage({ id: value }) : value;
  };

  renderOptions = () => {
    const { data, hoverTextColor, dataKey, dataValue } = this.props;
    const { selected } = this.state;
    const renderedList = [];
    map(data, (item) => {
      const optionId = uniqueId('child-');
      renderedList.push(
        item[dataKey] === selected ? (
          <SelectedItem
            key={optionId}
            hoverTextColor={hoverTextColor}
            id="focusedItem"
            onClick={() => this.handleOnClick(item[dataKey])}
          >
            {this.renderValue(item[dataValue])}
          </SelectedItem>
        ) : (
          <OptionContainer
            key={optionId}
            hoverTextColor={hoverTextColor}
            onClick={() => this.handleOnClick(item[dataKey])}
          >
            {this.renderValue(item[dataValue])}
          </OptionContainer>
        ),
      );
    });
    return renderedList;
  };

  renderGroupedOptions = () => {
    const {
      data,
      hoverTextColor,
      dataKey, dataValue,
    } = this.props;

    const { selected } = this.state;
    const renderedList = [];
    map(data, (item) => {
      renderedList.push(
        <OptionHeaderContainer key={uniqueId('parent-')}>{item[dataValue]}</OptionHeaderContainer>,
      );
      map(item.children, (child) => {
        const optionId = uniqueId('child-');
        renderedList.push(
          child[dataKey] === selected ? (
            <SelectedItem
              key={optionId}
              hoverTextColor={hoverTextColor}
              onClick={() => {
                this.handleOnClick(child[dataKey]);
              }}
            >
              {this.renderValue(child[dataValue])}
            </SelectedItem>
          ) : (
            <OptionContainer
              key={optionId}
              hoverTextColor={hoverTextColor}
              onClick={() => {
                this.handleOnClick(child[dataKey]);
              }}
            >
              {this.renderValue(child[dataValue])}
            </OptionContainer>
          ),
        );
      });
    });
    return renderedList;
  };

  render() {
    const {
      displayValue,
      label,
      grouped,
      background,
      color,
      hoverColor,
      height,
      tabIndex,
      width,
      error,
      headerFontSize,
      optionsFontSize,
      headerHeight,
      disabled,
      star,
    } = this.props;
    const { expanded, focused } = this.state;

    return (
      <SelectDropdownContainer width={width}>
        {label && (
          <LabelContainer>
            <StyledLabel>
              <FormattedMessage id={label} /> {star && <span style={{ color: star }}>*</span>}
            </StyledLabel>
          </LabelContainer>
        )}
        <div
          className="dropdown-box"
          role="listbox"
          tabIndex={tabIndex}
          onFocus={this.addKeyPressListener}
          onBlur={() => {
            this.clearSelected();
            this.removeKeyPressListener();
          }}
          ref={(multiSelectRef) => {
            this.multiSelectRef = multiSelectRef;
          }}
          style={{ outline: 'none' }}
        >
          <HeaderContainer
            background={background}
            color={color}
            hoverColor={hoverColor}
            expanded={expanded}
            error={error}
            focused={focused}
            headerFontSize={headerFontSize}
            disabled={disabled}
          >
            <SelectHeader
              onClick={() => this.toggleExpand()}
              value={displayValue}
              headerHeight={headerHeight}
              disabled={disabled}
            >
              {this.renderValue(displayValue)}
              {expanded ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
            </SelectHeader>
          </HeaderContainer>
          {expanded && (
            <OptionsContainer
              id="optionsContainer"
              background={background}
              color={color}
              height={height}
              expanded={expanded}
              onMouseOver={() => this.clearSelected()}
              onFocus={() => null}
              optionsFontSize={optionsFontSize}
            >
              <ul style={{ listStyleType: 'none', margin: 0, padding: 0 }}>
                {grouped
                  ? expanded && this.renderGroupedOptions()
                  : expanded && this.renderOptions()}
              </ul>
            </OptionsContainer>
          )}
        </div>
      </SelectDropdownContainer>
    );
  }
}

SelectDropdown.propTypes = {
  data: PropTypes.array,
  error: PropTypes.string,
  displayValue: PropTypes.string,
  grouped: PropTypes.bool,
  label: PropTypes.string,
  color: PropTypes.string,
  background: PropTypes.string,
  hoverColor: PropTypes.string,
  onClicked: PropTypes.func.isRequired,
  tabIndex: PropTypes.string,
  height: PropTypes.string,
  width: PropTypes.string,
  optionsFontSize: PropTypes.string,
  headerFontSize: PropTypes.string,
  headerHeight: PropTypes.string,
  disabled: PropTypes.bool,
  star: PropTypes.string,
  formatMessage: PropTypes.bool,
  intl: PropTypes.object.isRequired,
  hoverTextColor: PropTypes.string,
  dataKey: PropTypes.string,
  dataValue: PropTypes.string,
};

SelectDropdown.defaultProps = {
  data: [],
  displayValue: '',
  grouped: false,
  label: '',
  color: null,
  background: null,
  hoverColor: null,
  tabIndex: '0',
  height: null,
  width: null,
  error: null,
  optionsFontSize: null,
  headerFontSize: null,
  headerHeight: '22px',
  disabled: false,
  star: '',
  dataKey: 'key',
  dataValue: 'value',
  formatMessage: false,
  hoverTextColor: 'white',
};

export default injectIntl(SelectDropdown);
